import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import BackLink from "@/components/BackLink";
import PendingDraftBanner from "@/components/PendingDraftBanner";
import { Plus, Edit3, Eye, EyeOff, MapPin, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
function modHeaders() { return { Authorization: `Bearer ${sessionStorage.getItem("mod_token")}` }; }

export default function ModPraktiskt() {
  const [tab, setTab] = useState("vardagskort");
  const [vardagskort, setVardagskort] = useState([]);
  const [tjanstekort, setTjanstekort] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const fetch = useCallback(async () => {
    try {
      const [v, t] = await Promise.all([
        axios.get(`${API}/mod/vardagskort`, { headers: modHeaders() }),
        axios.get(`${API}/mod/tjanstekort`, { headers: modHeaders() }),
      ]);
      setVardagskort(v.data);
      setTjanstekort(t.data);
    } catch (err) { if (err.response?.status === 401) navigate("/mod/login"); }
    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    if (!sessionStorage.getItem("mod_token")) { navigate("/mod/login"); return; }
    fetch();
  }, [navigate, fetch]);

  const togglePublish = async (typ, item) => {
    setBusy(true);
    const ep = item.status === "Publicerad" ? "unpublish" : "publish";
    await axios.post(`${API}/mod/${typ}/${item.id}/${ep}`, {}, { headers: modHeaders() });
    await fetch(); setBusy(false);
  };

  const startEdit = (typ, item) => {
    setEditId(`${typ}:${item.id}`);
    if (typ === "vardagskort") {
      setEditForm({ rubrik: item.rubrik, beskrivning: item.beskrivning, ort: item.ort || "", omrade: item.omrade, kontakt_telefon: item.kontakt_telefon || "", kontakt_epost: item.kontakt_epost || "", extern_lank: item.extern_lank || "", kalltext: item.kalltext || "" });
    } else {
      setEditForm({ foretagsnamn: item.foretagsnamn, beskrivning: item.beskrivning, ort: item.ort || "", kontaktperson: item.kontaktperson || "", kontakt_telefon: item.kontakt_telefon || "", kontakt_epost: item.kontakt_epost || "", webbplats: item.webbplats || "", fraga_om_rut: item.fraga_om_rut || false, fraga_om_rot: item.fraga_om_rot || false });
    }
  };

  const saveEdit = async (typ, id) => {
    setBusy(true);
    await axios.put(`${API}/mod/${typ}/${id}/edit`, editForm, { headers: modHeaders() });
    setEditId(null); await fetch(); setBusy(false);
  };

  const items = tab === "vardagskort" ? vardagskort : tjanstekort;
  const createUrl = tab === "vardagskort" ? "/mod/praktiskt/skapa-vardagskort" : "/mod/praktiskt/skapa-tjanstekort";

  return (
    <div data-testid="mod-praktiskt-page" className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      <BackLink to="/mod" label="Moderatorvy" />
      <PendingDraftBanner urlPattern="/api/mod" onSuccess={() => fetch()} />
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl font-medium text-ink">Praktiskt i bygden</h1>
        <Link to={createUrl} data-testid="mod-praktiskt-create" className="inline-flex items-center gap-2 bg-pine hover:bg-pine-hover text-white font-medium text-sm px-4 py-2.5 rounded-md transition-colors no-underline">
          <Plus size={16} /> Lägg till
        </Link>
      </div>

      <div className="flex gap-1 mb-6 border-b border-warm-border">
        <button onClick={() => setTab("vardagskort")} data-testid="mod-tab-vardagskort" className={`px-4 py-3 text-base font-medium border-b-2 transition-colors ${tab === "vardagskort" ? "border-pine text-pine" : "border-transparent text-muted-foreground hover:text-ink"}`}>Vardagskort</button>
        <button onClick={() => setTab("tjanstekort")} data-testid="mod-tab-tjanstekort" className={`px-4 py-3 text-base font-medium border-b-2 transition-colors ${tab === "tjanstekort" ? "border-pine text-pine" : "border-transparent text-muted-foreground hover:text-ink"}`}>Tjänstekort</button>
      </div>

      {loading ? <p className="text-muted-foreground">Laddar...</p> : items.length === 0 ? (
        <div className="bg-bone-alt rounded-sm p-8 text-center"><p className="text-muted-foreground">Inga kort ännu.</p></div>
      ) : (
        <div className="flex flex-col gap-3">
          {items.map((item) => {
            const isEditing = editId === `${tab}:${item.id}`;
            const name = tab === "vardagskort" ? item.rubrik : item.foretagsnamn;
            return (
              <div key={item.id} data-testid={`mod-${tab}-${item.id}`} className="bg-white border border-warm-border rounded-sm p-4">
                {isEditing ? (
                  <EditForm typ={tab} form={editForm} setForm={setEditForm} onSave={() => saveEdit(tab, item.id)} onCancel={() => setEditId(null)} busy={busy} />
                ) : (
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-heading text-base font-medium text-ink">{name}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-sm ${item.status === "Publicerad" ? "bg-green-100 text-green-800" : "bg-stone-100 text-stone-600"}`}>{item.status}</span>
                        {item.fraga_om_rut && <span className="text-xs px-2 py-0.5 rounded-sm bg-amber-50 text-amber-700">RUT</span>}
                        {item.fraga_om_rot && <span className="text-xs px-2 py-0.5 rounded-sm bg-amber-50 text-amber-700">ROT</span>}
                      </div>
                      {item.omrade && <p className="text-sm text-muted-foreground">{item.omrade}{item.ort ? ` · ${item.ort}` : ""}</p>}
                      <p className="text-sm text-muted-foreground mt-1">{item.beskrivning}</p>
                    </div>
                    <div className="flex gap-2 ml-3 flex-shrink-0">
                      <button onClick={() => startEdit(tab, item)} className="p-2 text-muted-foreground hover:text-pine transition-colors"><Edit3 size={16} /></button>
                      <button onClick={() => togglePublish(tab, item)} disabled={busy} className="p-2 text-muted-foreground hover:text-falu transition-colors">
                        {item.status === "Publicerad" ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function EditForm({ typ, form, setForm, onSave, onCancel, busy }) {
  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));
  return (
    <div className="space-y-3">
      {typ === "vardagskort" ? (
        <>
          <input value={form.rubrik} onChange={e => set("rubrik", e.target.value)} className="input-mod" placeholder="Rubrik" />
          <textarea value={form.beskrivning} onChange={e => set("beskrivning", e.target.value)} rows={2} className="input-mod" placeholder="Beskrivning" />
          <select value={form.omrade} onChange={e => set("omrade", e.target.value)} className="input-mod">
            <option>Handla & vardag</option><option>Vård & trygghet</option>
          </select>
          <input value={form.ort} onChange={e => set("ort", e.target.value)} className="input-mod" placeholder="Ort" />
          <input value={form.extern_lank} onChange={e => set("extern_lank", e.target.value)} className="input-mod" placeholder="Extern länk" />
          <input value={form.kalltext} onChange={e => set("kalltext", e.target.value)} className="input-mod" placeholder="Källa" />
        </>
      ) : (
        <>
          <input value={form.foretagsnamn} onChange={e => set("foretagsnamn", e.target.value)} className="input-mod" placeholder="Företagsnamn" />
          <textarea value={form.beskrivning} onChange={e => set("beskrivning", e.target.value)} rows={2} className="input-mod" placeholder="Beskrivning" />
          <input value={form.ort} onChange={e => set("ort", e.target.value)} className="input-mod" placeholder="Ort" />
          <input value={form.kontakt_telefon} onChange={e => set("kontakt_telefon", e.target.value)} className="input-mod" placeholder="Telefon" />
          <input value={form.kontakt_epost} onChange={e => set("kontakt_epost", e.target.value)} className="input-mod" placeholder="E-post" />
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.fraga_om_rut} onChange={e => set("fraga_om_rut", e.target.checked)} className="accent-pine" /> Fråga om RUT</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.fraga_om_rot} onChange={e => set("fraga_om_rot", e.target.checked)} className="accent-pine" /> Fråga om ROT</label>
          </div>
        </>
      )}
      <div className="flex gap-3">
        <button onClick={onSave} disabled={busy} className="bg-pine hover:bg-pine-hover text-white font-medium px-4 py-2 rounded-md text-sm transition-colors">Spara</button>
        <button onClick={onCancel} className="text-muted-foreground text-sm">Avbryt</button>
      </div>
    </div>
  );
}
